import { Box, StarFilledIcon, Typography } from '@iheartjane/reefer';
import type { PaddingProperties } from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { productCardTextDetails } from '../../utils/productCard/textDetails';
import type { TextDetailsArgs } from '../../utils/productCard/textDetails';

import styles from './cardText.module.css';

export interface CardTextProps extends TextDetailsArgs, PaddingProperties {
  ratingPlacement?: 'firstLine' | 'belowName';
  /**
   * Indicates product rating (out of 5 stars) and number of reviews.
   * @example "4.9 (1.2k)"
   */
  ratingSummary?: Nullish<string>;
  /**
   * Render prop for the product category label (first line).
   * Receives a classname to apply to the label container, to align text.
   */
  renderProductCategoryLabel: ({
    className,
  }: {
    className: string;
  }) => ReactNode;
}

export const CardText = ({
  brandInfo,
  dosageInfo,
  hasMultipleWeightDiscount,
  isSponsored,
  name,
  potencyInfo,
  ratingPlacement = 'firstLine',
  ratingSummary,
  renderProductCategoryLabel,
  ...paddingProps
}: CardTextProps) => {
  const lines = useMemo(
    () =>
      productCardTextDetails({
        brandInfo,
        dosageInfo,
        hasMultipleWeightDiscount,
        isSponsored,
        name,
        potencyInfo,
      }),
    [
      brandInfo,
      dosageInfo,
      hasMultipleWeightDiscount,
      isSponsored,
      name,
      potencyInfo,
    ]
  );

  const stacked = ratingPlacement === 'belowName';

  const rows: ReactNode[] = lines.map(
    ({ lineId, text, ...typographyProps }, index) => (
      <Typography
        className={clsx(styles['line--full'], {
          [styles['menuGutter']]: index === 0,
        })}
        key={lineId}
        {...typographyProps}
      >
        {text}
      </Typography>
    )
  );

  if (ratingSummary) {
    const nameLinePosition = lines.findIndex(({ lineId }) => lineId === 'name');

    const insertAt =
      stacked && nameLinePosition !== -1 ? nameLinePosition + 1 : 0;

    rows.splice(
      insertAt,
      0,
      <div className={styles['reviews']} key="rating">
        <StarFilledIcon color="gold" size="sm" mr={2} />
        <Typography maxLines={1} variant="small-body-regular" color="text-main">
          {ratingSummary}
        </Typography>
      </div>
    );
  }

  return (
    <Box
      className={clsx(styles['cardText'], {
        [styles['cardText__stacked']]: stacked,
      })}
      {...paddingProps}
    >
      {renderProductCategoryLabel({
        className: clsx(styles['line--first'], styles['menuGutter']),
      })}
      {rows}
    </Box>
  );
};
